// A curated set of real, land-based towns and suburbs across Australia and
// New Zealand, used to pick a random starting location for the map on load.
// Coordinates are hand-picked town/CBD centres (not random points in a
// bounding box) so every entry is guaranteed to be on land.

export interface RandomLocation {
  name: string;
  address: string;
  coords: [number, number]; // [lng, lat]
}

interface RawEntry {
  name: string;
  region: string; // AU state abbreviation, or 'NZ'
  coords: [number, number];
}

const RAW_LOCATIONS: RawEntry[] = [
  // Australia
  { name: 'Fitzroy', region: 'VIC', coords: [144.9784, -37.8023] },
  { name: 'Katoomba', region: 'NSW', coords: [150.3117, -33.7147] },
  { name: 'Ballarat', region: 'VIC', coords: [143.8503, -37.5622] },
  { name: 'Toowoomba', region: 'QLD', coords: [151.9507, -27.5598] },
  { name: 'Alice Springs', region: 'NT', coords: [133.8807, -23.698] },
  { name: 'Broken Hill', region: 'NSW', coords: [141.4665, -31.9539] },
  { name: 'Bendigo', region: 'VIC', coords: [144.2794, -36.757] },
  { name: 'Wagga Wagga', region: 'NSW', coords: [147.3598, -35.1082] },
  { name: 'Dubbo', region: 'NSW', coords: [148.6011, -32.2569] },
  { name: 'Orange', region: 'NSW', coords: [149.0996, -33.2839] },
  { name: 'Armidale', region: 'NSW', coords: [151.6656, -30.5083] },
  { name: 'Tamworth', region: 'NSW', coords: [150.9294, -31.0927] },
  { name: 'Wangaratta', region: 'VIC', coords: [146.3255, -36.3591] },
  { name: 'Shepparton', region: 'VIC', coords: [145.4041, -36.3833] },
  { name: 'Mildura', region: 'VIC', coords: [142.1624, -34.1855] },
  { name: 'Warrnambool', region: 'VIC', coords: [142.4864, -38.3818] },
  { name: 'Horsham', region: 'VIC', coords: [142.1974, -36.7159] },
  { name: 'Kalgoorlie', region: 'WA', coords: [121.4655, -30.7489] },
  { name: 'Geraldton', region: 'WA', coords: [114.6084, -28.7774] },
  { name: 'Albany', region: 'WA', coords: [117.8837, -35.0269] },
  { name: 'Mount Gambier', region: 'SA', coords: [140.7794, -37.8283] },
  { name: 'Whyalla', region: 'SA', coords: [137.547, -33.0333] },
  { name: 'Port Augusta', region: 'SA', coords: [137.7629, -32.4927] },
  { name: 'Rockhampton', region: 'QLD', coords: [150.51, -23.3791] },
  { name: 'Mackay', region: 'QLD', coords: [149.1868, -21.155] },
  { name: 'Bundaberg', region: 'QLD', coords: [152.3489, -24.8661] },
  { name: 'Fremantle', region: 'WA', coords: [115.7581, -32.0569] },
  { name: 'Ballina', region: 'NSW', coords: [153.5667, -28.8667] },
  { name: 'Lismore', region: 'NSW', coords: [153.2777, -28.8135] },
  { name: 'Nagambie', region: 'VIC', coords: [145.15, -36.7833] },
  { name: 'Cootamundra', region: 'NSW', coords: [148.0324, -34.6438] },
  { name: 'Griffith', region: 'NSW', coords: [146.0654, -34.2871] },
  { name: 'Warwick', region: 'QLD', coords: [152.0344, -28.2166] },
  { name: 'Gympie', region: 'QLD', coords: [152.665, -26.19] },

  // New Zealand
  { name: 'Cambridge', region: 'NZ', coords: [175.4667, -37.8833] },
  { name: 'Matamata', region: 'NZ', coords: [175.7667, -37.8117] },
  { name: 'Blenheim', region: 'NZ', coords: [173.9612, -41.5134] },
  { name: 'Nelson', region: 'NZ', coords: [173.284, -41.2706] },
  { name: 'Timaru', region: 'NZ', coords: [171.2543, -44.3904] },
  { name: 'Ashburton', region: 'NZ', coords: [171.7514, -43.9038] },
  { name: 'Gisborne', region: 'NZ', coords: [178.023, -38.6624] },
  { name: 'Whanganui', region: 'NZ', coords: [175.0475, -39.9301] },
  { name: 'Masterton', region: 'NZ', coords: [175.6577, -40.9538] },
  { name: 'Feilding', region: 'NZ', coords: [175.5654, -40.2298] },
  { name: 'Cromwell', region: 'NZ', coords: [169.1974, -45.041] },
  { name: 'Alexandra', region: 'NZ', coords: [169.39, -45.2537] },
  { name: 'Oamaru', region: 'NZ', coords: [170.97, -45.0968] },
  { name: 'Levin', region: 'NZ', coords: [175.283, -40.6229] },
  { name: 'Taupo', region: 'NZ', coords: [176.07, -38.6857] },
  { name: 'Rotorua', region: 'NZ', coords: [176.25, -38.1368] },
  { name: 'Tauranga', region: 'NZ', coords: [176.1651, -37.6878] },
  { name: 'Hastings', region: 'NZ', coords: [176.8397, -39.6398] },
  { name: 'Napier', region: 'NZ', coords: [176.912, -39.4928] },
  { name: 'Palmerston North', region: 'NZ', coords: [175.6082, -40.3523] },
  { name: 'New Plymouth', region: 'NZ', coords: [174.0752, -39.0556] },
  { name: 'Whangarei', region: 'NZ', coords: [174.3237, -35.7275] },
  { name: 'Pukekohe', region: 'NZ', coords: [174.9017, -37.2004] },
  { name: 'Greymouth', region: 'NZ', coords: [171.2, -42.45] },
  { name: 'Queenstown', region: 'NZ', coords: [168.6626, -45.0312] },
  { name: 'Wanaka', region: 'NZ', coords: [169.1321, -44.7] },
];

function formatAddress(entry: RawEntry): string {
  return entry.region === 'NZ'
    ? `${entry.name}, New Zealand`
    : `${entry.name}, ${entry.region}, Australia`;
}

export const AU_NZ_LOCATIONS: RandomLocation[] = RAW_LOCATIONS.map((entry) => ({
  name: entry.name,
  address: formatAddress(entry),
  coords: entry.coords,
}));

export function getRandomLocation(): RandomLocation {
  return AU_NZ_LOCATIONS[Math.floor(Math.random() * AU_NZ_LOCATIONS.length)];
}
